import { useEffect } from 'react';
import { publicSettings } from '../services/publicSettings';

// Google Fonts stylesheets — loaded on demand so users get the exact typeface
// the admin picked without shipping every font upfront. The stylesheet <link>
// is inserted once and stays in the DOM even if the admin later flips back to
// 'system' (harmless cache).
const FONT_HREFS = {
  Inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap',
  Poppins: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap',
  Roboto: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap',
};

const FONT_STACKS = {
  system: 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  Inter: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
  Poppins: 'Poppins, system-ui, -apple-system, "Segoe UI", sans-serif',
  Roboto: 'Roboto, system-ui, -apple-system, "Segoe UI", sans-serif',
};

const injectFontLink = (href) => {
  if (!href) return;
  const existing = document.querySelector(`link[data-brand-font="${href}"]`);
  if (existing) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.setAttribute('data-brand-font', href);
  document.head.appendChild(link);
};

/**
 * BrandThemeApplier — headless. Reads platform_settings on mount and applies
 * `--brand-primary`, `--brand-accent` and `--brand-font-family` CSS variables
 * on `<html>` so anywhere in the app can pick them up. Also loads the chosen
 * Google Font stylesheet if it isn't the system font.
 */
const BrandThemeApplier = () => {
  useEffect(() => {
    let cancelled = false;
    publicSettings.get().then((s) => {
      if (cancelled || !s) return;
      const root = document.documentElement;
      if (s.brand_primary_color) root.style.setProperty('--brand-primary', s.brand_primary_color);
      if (s.brand_accent_color) root.style.setProperty('--brand-accent', s.brand_accent_color);
      const font = s.brand_font_family || 'system';
      if (font !== 'system' && FONT_HREFS[font]) injectFontLink(FONT_HREFS[font]);
      const stack = FONT_STACKS[font] || FONT_STACKS.system;
      root.style.setProperty('--brand-font-family', stack);
      // Apply directly to body so Tailwind's font-sans utility picks it up too.
      document.body.style.fontFamily = stack;
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return null;
};

export default BrandThemeApplier;
